"use client"

import { useEffect, useState } from "react"

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";

import LeftPanel from "./components/LeftPanel"
import RightPanel from "./components/RightPanel"
import type { DocumentState, Term } from "./types"
import { getContentsTree, getAttachmentContent, processingContentsNode } from "../api/admin"

import theme from "../style/theme";
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { setMenu } from "../reducer/menu"
import { ThemeProvider } from "@mui/material/styles";

import { openAlert } from '../reducer/alert'
import { store } from '../store/AriStore'


function App() {
  const [documentState, setDocumentState] = useState<DocumentState>({ attachmentId: "", content: "", status: "draft", terms: [], fileName: null, fileType: null })
  const [terms, setTerms] = useState<Term[]>([])
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null)
  const [fileOptions, setFileOptions] = useState<{ id: string, name: string }[]>([])
  const [selectedFileId, setSelectedFileId] = useState<string>("")
  const [contentsId, setContentsId] = useState<string>("")
  const [contentsTitle, setContentsTitle] = useState<string>("")
  const [contentsType, setContentsType] = useState<"folder" | "file" | "">("")
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const userInfo = useSelector((state: any) => state.user);

  const handleDocumentChange = (changes: Partial<DocumentState>) => {
    setDocumentState((prev) => ({ ...prev, ...changes }))
  }

  const handleTermSelect = (term: Term) => {
    setSelectedTerm(term)
  }

  const handleTermUpdate = (updatedTerm: Term) => {
    setTerms((prev) => prev.map((term) => (term.termsId === updatedTerm.termsId ? updatedTerm : term)))
    setSelectedTerm(updatedTerm)
  }

  const handleTermDelete = (deletedTerm: Term) => {
    setTerms((prev) => prev.filter((term) => term.termsId !== deletedTerm.termsId))
    setSelectedTerm(null)
  }

  const handleTermAdd = () => {
    const newTerm: Term = { termsId: Date.now().toString(), terms: "새 용어", status: "draft", altTerms: [], descryption: "" }
    setTerms((prev) => [...prev, newTerm])
    setSelectedTerm(newTerm)
  }

  const handleSave = async () => {
    try {
      if (!contentsId) {
        // 전역 알림 다이얼로그로 대체
        store.dispatch(openAlert({ message: "선택된 컨텐츠가 없습니다. 목록에서 항목을 다시 선택해주세요." }))
        return
      }
      
      await processingContentsNode(contentsId, {
        id: contentsId,
        title: contentsTitle,
        type: contentsType || undefined,
        status: userInfo.role === "admin" ? "approve" : "request",
        contents: documentState.content,
        ...(documentState.attachmentId ? { attachmentId: documentState.attachmentId } : {}),
        terms: terms.map(t => ({
          termsId: typeof t.termsId === 'number' ? t.termsId : Number(t.termsId),
          terms: t.terms,
          altTerms: Array.isArray(t.altTerms) ? (t.altTerms.length ? t.altTerms.join(',') : null) : t.altTerms,
          descryption: t.descryption,
        })),
      })

      {
        const { openAlert } = await import('../reducer/alert')
        const { store } = await import('../store/AriStore')
        store.dispatch(openAlert({ message: "저장 및 발행요청 처리되었습니다." }))
      }
    } catch (e: any) {
      {
        const { openAlert } = await import('../reducer/alert')
        const { store } = await import('../store/AriStore')
        store.dispatch(openAlert({ message: e?.message || "발행요청 처리에 실패했습니다." }))
      }
    }
  }

  const handleSaveDraft = async () => {
    try {
      if (!contentsId) {
        // 전역 알림 다이얼로그로 대체
        store.dispatch(openAlert({ message: "선택된 컨텐츠가 없습니다. 목록에서 항목을 다시 선택해주세요." }))
        return
      }
      
      await processingContentsNode(contentsId, {
        id: contentsId,
        title: contentsTitle,
        type: contentsType || undefined,
        status: "save",
        contents: documentState.content,
        ...(documentState.attachmentId ? { attachmentId: documentState.attachmentId } : {}),
        terms: terms.map(t => ({
          termsId: typeof t.termsId === 'number' ? t.termsId : Number(t.termsId),
          terms: t.terms,
          altTerms: Array.isArray(t.altTerms) ? (t.altTerms.length ? t.altTerms.join(',') : null) : t.altTerms,
          descryption: t.descryption,
        })),
      })

      {
        const { openAlert } = await import('../reducer/alert')
        const { store } = await import('../store/AriStore')
        store.dispatch(openAlert({ message: "임시 저장 처리되었습니다." }))
      }
    } catch (e: any) {
      {
        const { openAlert } = await import('../reducer/alert')
        const { store } = await import('../store/AriStore')
        store.dispatch(openAlert({ message: e?.message || "임시 저장 처리에 실패했습니다." }))
      }
    }
  }

  useEffect(() => {
    const init = async () => {
      const idFromQuery = searchParams.get("id") || ""
      setContentsId(idFromQuery)
      setSelectedFileId(idFromQuery)

      // 1) 트리 데이터에서 idFromQuery로 노드 찾고 첨부파일 목록으로 파일 선택 옵션 구성
        const tree = await getContentsTree()

        const findNode = (nodes: any[], id: string): any | null => {
          for (const n of nodes) {
            if (String(n.id) === String(id)) return n
            if (n.children) {
              const f = findNode(n.children, id)
              if (f) return f
            }
          }
          return null
        }
        const node = idFromQuery ? findNode(Array.isArray(tree) ? tree : [], idFromQuery) : null
        if (node) {
          setContentsId(String(node.id))
          setContentsTitle(String(node.title || ""))
          setContentsType(node.type === "folder" ? "folder" : "file")
        } else {
          setContentsTitle("")
          setContentsType("")
        }
        const attachments: any[] = node?.attachments || []
        const options = attachments.map(att => ({ id: String(att.id), name: att.fileName }))
        setFileOptions(options)

        // 2) 첫 번째 항목을 기본 선택으로 설정하고 데이터 로드
        const initial = options[0]?.id || ""
        const attachmentId = idFromQuery && options.some(o => o.id === idFromQuery) ? idFromQuery : initial
        if (attachmentId) {
          try {
            const data = await getAttachmentContent(attachmentId)
            setSelectedFileId(attachmentId)
            const mappedTerms: Term[] = (data.terms || []).map((t) => ({ termsId: t.termsId, terms: t.terms, status: t.status || "draft", altTerms: t.altTerms || [], descryption: t.descryption || "" }))
            setDocumentState({ attachmentId: data.attachmentId, content: data.content || "", status: data.status || "draft", terms: mappedTerms, fileName: data.fileName || null, fileType: data.fileType?.includes("pdf") ? "pdf" : (data.fileType?.includes("png") ? "png" : null) })
            setTerms(mappedTerms)
            setSelectedTerm(mappedTerms[0] || null) 
          } catch (e) {
            setDocumentState({ attachmentId: attachmentId, content: "", status: "draft", terms: [], fileName: null, fileType: null })
            setTerms([])
            setSelectedTerm(null)
          }
        } else {
          setDocumentState({ attachmentId: attachmentId, content: "", status: "draft", terms: [], fileName: null, fileType: null })
          setTerms([])
          setSelectedTerm(null)
        }
    }
    void init()
  }, [searchParams])

  const handleBackToList = () => {
    dispatch(setMenu({ index: 920, name: "지식 관리", welcome: "" }))
    navigate("/console/file-tree")
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ height: "54px", px: 2, py: 1, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel id="dm-file-select-label">파일 선택</InputLabel>
            <Select
              labelId="dm-file-select-label"
              label="파일 선택"
              value={selectedFileId}
              onChange={async (e) => {
                const newId = e.target.value as string
                setSelectedFileId(newId)
                try {
                  if (newId) {
                    const data = await getAttachmentContent(newId)
                    const mappedTerms: Term[] = (data.terms || []).map((t) => ({ termsId: String(t.termsId), terms: t.terms, status: t.status || "draft", altTerms: t.altTerms || [], descryption: t.descryption || "" }))
                    setDocumentState({ attachmentId: data.attachmentId, content: data.content || "", status: data.status || "draft", terms: mappedTerms, fileName: data.fileName || null, fileType: data.fileType?.includes("pdf") ? "pdf" : (data.fileType?.includes("png") ? "png" : null) })
                    setTerms(mappedTerms)
                    setSelectedTerm(mappedTerms[0] || null)
                  } else {
                    setDocumentState({ attachmentId: newId, content: "", status: "draft", terms: [], fileName: null, fileType: null })
                    setTerms([])
                    setSelectedTerm(null)
                  }
                } catch (err) {
                  setDocumentState({ attachmentId: newId, content: "", status: "draft", terms: [], fileName: null, fileType: null })
                  setTerms([])
                  setSelectedTerm(null)
                }
              }}
            >
              {fileOptions.length === 0 ? (
                <MenuItem key="empty" value="">파일을 선택하세요</MenuItem>
              ) : (
                fileOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleBackToList}>목록으로</Button>
            <Button variant="outlined" color="secondary" onClick={handleSaveDraft}>{"임시 저장"}</Button>
            <Button variant="contained" color="secondary" onClick={handleSave}>{userInfo.role === "admin" ? "저장 & 발행" : "저장 & 발행요청"}</Button>
          </Box>
        </Box>
        <Box sx={{ height: "calc(100dvh - 54px)", flex: 1, display: "flex", overflow: "hidden" }}>
          <Box sx={{ width: "60%", height: "100vh", borderRight: 1, borderColor: "divider" }}>
            <LeftPanel documentState={documentState} onDocumentChange={handleDocumentChange}/>
          </Box>
          <Box sx={{ width: "40%", height: "100vh" }}>
            <RightPanel
              terms={terms}
              selectedTerm={selectedTerm}
              onTermSelect={handleTermSelect}
              onTermDelete={handleTermDelete}
              onTermUpdate={handleTermUpdate}
              onTermAdd={handleTermAdd}
              onSave={handleSave}
              attachmentId={documentState.attachmentId}
            />
          </Box>
        </Box>
        {/* 로딩 백드롭 (모달) */}
        {/* <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
          <Typography sx={{ ml: 2 }}>데이터를 불러오는 중...</Typography>
        </Backdrop> */}
      </Box>
    </ThemeProvider>
  )
}

export default App